import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useCreateCustomerPaymentMethodSetupIntent, useConfirmCustomerPaymentMethod } from '@/hooks/customer/useCustomerPaymentMethod';
import { useCustomerSubscriptionCheckoutContext } from '@/hooks/customer/useCustomerSubscriptionCheckout';
import { useResolvedTheme } from '@/hooks/useResolvedTheme';
import { extractJsonApiErrorMessage, extractJsonApiErrorCode } from '@/lib/jsonapi/errors';
import { buildStripeAppearance } from '@/lib/stripe/appearance';
import { isStripeReady, resolveStripeAccountOption } from '@/lib/stripe/isStripeReady';
import { resolveStripeLocale } from '@/lib/stripe/locale';
import type { CustomerPaymentMethodSetupIntent } from '@/types/customer-payment-method';
import type { CustomerCheckoutStripeConfig } from '@/types/customer-subscription-checkout';

interface PaymentMethodSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'add' | 'update';
}

function StripeSetupForm({
  setupIntent,
  onConfirmed,
  onCancel,
}: {
  setupIntent: CustomerPaymentMethodSetupIntent;
  onConfirmed: (setupIntentId: string) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isElementReady, setIsElementReady] = useState(false);
  const [elementError, setElementError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    setIsSubmitting(true);
    try {
      const submitResult = await elements.submit();
      if (submitResult.error) {
        toast.error(submitResult.error.message ?? t('customer.billing.paymentMethod.dialog.errors.confirm'));
        return;
      }

      const result = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.href },
      });

      if (result.error) {
        toast.error(result.error.message ?? t('customer.billing.paymentMethod.dialog.errors.confirm'));
        return;
      }

      const intentId = result.setupIntent?.id ?? setupIntent.intentId;
      const status = result.setupIntent?.status;
      if (!intentId || status !== 'succeeded') {
        toast.error(t('customer.billing.paymentMethod.dialog.errors.notSucceeded'));
        return;
      }

      await onConfirmed(intentId);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {elementError ? (
        <Alert variant="destructive">
          <AlertTitle>{t('customer.billing.paymentMethod.dialog.errors.elementLoadTitle')}</AlertTitle>
          <AlertDescription>{elementError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-2">
        {!isElementReady ? (
          <p className="text-xs text-muted-foreground">{t('customer.billing.paymentMethod.dialog.loadingElement')}</p>
        ) : null}
        <div className="rounded-lg border bg-muted/10 p-4">
          <PaymentElement
            onReady={() => setIsElementReady(true)}
            onLoadError={(event) => {
              const message = event.error?.message?.trim();
              setElementError(message || t('customer.billing.paymentMethod.dialog.errors.elementLoad'));
            }}
          />
        </div>
      </div>

      <DialogFooter className="flex-col gap-2 sm:flex-row">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {t('customer.billing.paymentMethod.dialog.cancel')}
        </Button>
        <Button type="submit" disabled={!stripe || !elements || isSubmitting}>
          {isSubmitting
            ? t('customer.billing.paymentMethod.dialog.processing')
            : t('customer.billing.paymentMethod.dialog.confirm')}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function PaymentMethodSetupDialog({ open, onOpenChange, mode }: PaymentMethodSetupDialogProps) {
  const { t, i18n } = useTranslation();
  const theme = useResolvedTheme();

  const checkoutContextQuery = useCustomerSubscriptionCheckoutContext(undefined, open);
  const createSetupIntentMutation = useCreateCustomerPaymentMethodSetupIntent();
  const confirmMutation = useConfirmCustomerPaymentMethod();

  const [setupIntent, setSetupIntent] = useState<CustomerPaymentMethodSetupIntent | null>(null);
  const [setupIntentError, setSetupIntentError] = useState<string | null>(null);

  // React 18 StrictMode intentionally double-invokes effects in dev.
  // Guard to ensure we only request a setup intent once per dialog open.
  const requestedRef = useRef(false);
  const setupIntentRequestIdRef = useRef(0);

  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [stripeLoadError, setStripeLoadError] = useState<string | null>(null);
  const [intentValidationError, setIntentValidationError] = useState<string | null>(null);
  const validatedSecretRef = useRef<string | null>(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) {
      requestedRef.current = false;
      setupIntentRequestIdRef.current += 1;
      setSetupIntent(null);
      setSetupIntentError(null);
      setIntentValidationError(null);
      validatedSecretRef.current = null;
      createSetupIntentMutation.reset();
      confirmMutation.reset();
      return;
    }

    if (requestedRef.current) {
      return;
    }
    requestedRef.current = true;
    const requestId = setupIntentRequestIdRef.current + 1;
    setupIntentRequestIdRef.current = requestId;

    (async () => {
      try {
        setSetupIntentError(null);
        const intent = await createSetupIntentMutation.mutateAsync();
        if (!isMountedRef.current || setupIntentRequestIdRef.current !== requestId) return;
        setSetupIntent(intent);
      } catch (error) {
        if (!isMountedRef.current || setupIntentRequestIdRef.current !== requestId) return;
        const code = extractJsonApiErrorCode(error);
        if (code === 'stripe_not_configured') {
          setSetupIntentError(t('customer.billing.paymentMethod.stripeNotConfigured'));
          return;
        }
        setSetupIntentError(
          extractJsonApiErrorMessage(error, t('customer.billing.paymentMethod.dialog.errors.setupIntent')),
        );
      }
    })();
  }, [open, createSetupIntentMutation, confirmMutation, t]);

  const stripeConfig: CustomerCheckoutStripeConfig | null = (() => {
    const candidate = checkoutContextQuery.data?.stripe ?? null;
    if (candidate?.publishableKey) return candidate;

    const envKey =
      typeof import.meta.env.VITE_STRIPE_PUBLIC_KEY === 'string' ? import.meta.env.VITE_STRIPE_PUBLIC_KEY : '';
    const normalizedEnvKey = envKey.trim();
    if (!normalizedEnvKey) return candidate;

    return {
      publishableKey: normalizedEnvKey,
      accountId: null,
      accountType: null,
    };
  })();

  const publishableKey = stripeConfig?.publishableKey?.trim() ?? '';
  const stripeAccountOption = resolveStripeAccountOption(stripeConfig);
  const stripeLocale = resolveStripeLocale(i18n.language ?? 'en');

  const connectMisconfigured =
    (stripeConfig?.accountType?.trim().toLowerCase() === 'connect' && !stripeConfig?.accountId?.trim()) ?? false;

  const insecureOrigin =
    typeof window !== 'undefined' &&
    window.location.protocol !== 'https:' &&
    window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1';

  const stripePromise = useMemo(() => {
    if (!isStripeReady(stripeConfig)) return null;
    return loadStripe(publishableKey, stripeAccountOption);
  }, [publishableKey, stripeAccountOption?.stripeAccount, stripeConfig?.accountType]);

  useEffect(() => {
    if (!open) {
      setStripeInstance(null);
      setStripeLoadError(null);
      return;
    }

    if (!stripePromise) {
      setStripeInstance(null);
      setStripeLoadError(null);
      return;
    }

    let active = true;
    (async () => {
      try {
        const resolved = await stripePromise;
        if (!active || !isMountedRef.current) return;
        if (!resolved) {
          setStripeInstance(null);
          setStripeLoadError(t('customer.billing.paymentMethod.dialog.errors.stripeLoad'));
          return;
        }
        setStripeLoadError(null);
        setStripeInstance(resolved);
      } catch {
        if (!active || !isMountedRef.current) return;
        setStripeInstance(null);
        setStripeLoadError(t('customer.billing.paymentMethod.dialog.errors.stripeLoad'));
      }
    })();

    return () => {
      active = false;
    };
  }, [open, stripePromise, t]);

  const titleKey =
    mode === 'add'
      ? 'customer.billing.paymentMethod.dialog.addTitle'
      : 'customer.billing.paymentMethod.dialog.updateTitle';

  const descriptionKey =
    mode === 'add'
      ? 'customer.billing.paymentMethod.dialog.addDescription'
      : 'customer.billing.paymentMethod.dialog.updateDescription';

  const appearance = buildStripeAppearance(theme);
  const clientSecret = setupIntent?.clientSecret?.trim() ?? '';

  useEffect(() => {
    if (!open) return;
    if (!stripeInstance) return;
    if (!clientSecret) return;
    if (validatedSecretRef.current === clientSecret) return;

    validatedSecretRef.current = clientSecret;
    setIntentValidationError(null);

    let active = true;
    (async () => {
      const retrieveSetupIntent = (stripeInstance as unknown as { retrieveSetupIntent?: (secret: string) => Promise<{ error?: { message?: string } }> })
        .retrieveSetupIntent;
      if (typeof retrieveSetupIntent !== 'function') {
        return;
      }

      try {
        const result = await retrieveSetupIntent(clientSecret);
        if (!active || !isMountedRef.current) return;
        const message = result.error?.message?.trim();
        if (message) {
          setIntentValidationError(message);
        }
      } catch {
        if (!active || !isMountedRef.current) return;
        setIntentValidationError(t('customer.billing.paymentMethod.dialog.errors.intentValidate'));
      }
    })();

    return () => {
      active = false;
    };
  }, [clientSecret, open, stripeInstance, t]);

  const ready =
    Boolean(open) &&
    Boolean(setupIntent) &&
    Boolean(clientSecret) &&
    Boolean(stripeInstance) &&
    !checkoutContextQuery.isLoading &&
    !createSetupIntentMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(titleKey)}</DialogTitle>
          <DialogDescription>{t(descriptionKey)}</DialogDescription>
        </DialogHeader>

        {checkoutContextQuery.isError ? (
          <Alert variant="destructive">
            <AlertTitle>{t('customer.billing.paymentMethod.dialog.errors.contextTitle')}</AlertTitle>
            <AlertDescription>
              {extractJsonApiErrorMessage(
                checkoutContextQuery.error,
                t('customer.billing.paymentMethod.dialog.errors.context'),
              )}
            </AlertDescription>
          </Alert>
        ) : null}

        {!publishableKey ? (
          <Alert variant="destructive">
            <AlertTitle>{t('customer.billing.paymentMethod.dialog.errors.missingStripeTitle')}</AlertTitle>
            <AlertDescription>{t('customer.billing.paymentMethod.dialog.errors.missingStripe')}</AlertDescription>
          </Alert>
        ) : null}

        {connectMisconfigured ? (
          <Alert variant="destructive">
            <AlertTitle>{t('customer.billing.paymentMethod.dialog.errors.connectTitle')}</AlertTitle>
            <AlertDescription>{t('customer.billing.paymentMethod.dialog.errors.connect')}</AlertDescription>
          </Alert>
        ) : null}

        {insecureOrigin ? (
          <Alert variant="destructive">
            <AlertTitle>{t('customer.billing.paymentMethod.dialog.errors.httpsTitle')}</AlertTitle>
            <AlertDescription>{t('customer.billing.paymentMethod.dialog.errors.https')}</AlertDescription>
          </Alert>
        ) : null}

        {stripeLoadError ? (
          <Alert variant="destructive">
            <AlertTitle>{t('customer.billing.paymentMethod.dialog.errors.stripeLoadTitle')}</AlertTitle>
            <AlertDescription>{stripeLoadError}</AlertDescription>
          </Alert>
        ) : null}

        {intentValidationError ? (
          <Alert variant="destructive">
            <AlertTitle>{t('customer.billing.paymentMethod.dialog.errors.intentValidateTitle')}</AlertTitle>
            <AlertDescription>{intentValidationError}</AlertDescription>
          </Alert>
        ) : null}

        {setupIntentError ? (
          <Alert variant="destructive">
            <AlertTitle>{t('customer.billing.paymentMethod.dialog.errors.setupIntentTitle')}</AlertTitle>
            <AlertDescription>{setupIntentError}</AlertDescription>
          </Alert>
        ) : null}

        {!setupIntentError && open && !setupIntent ? (
          <div className="space-y-3">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-28 w-full" />
            <div className="flex justify-end gap-2">
              <Skeleton className="h-9 w-28" />
              <Skeleton className="h-9 w-28" />
            </div>
          </div>
        ) : null}

        {ready ? (
          <Elements
            stripe={stripeInstance!}
            options={{
              clientSecret,
              appearance,
              locale: stripeLocale,
            }}
          >
            <StripeSetupForm
              setupIntent={setupIntent!}
              onCancel={() => onOpenChange(false)}
              onConfirmed={async (setupIntentId) => {
                try {
                  await confirmMutation.mutateAsync({ setup_intent_id: setupIntentId });
                  toast.success(t('customer.billing.paymentMethod.feedback.updated'));
                  onOpenChange(false);
                } catch (error) {
                  const code = extractJsonApiErrorCode(error);
                  if (code === 'setup_intent_not_succeeded') {
                    toast.error(t('customer.billing.paymentMethod.feedback.finishStripe'));
                    return;
                  }
                  if (code === 'setup_intent_customer_mismatch') {
                    toast.error(t('customer.billing.paymentMethod.feedback.refreshRequired'));
                    return;
                  }
                  toast.error(
                    extractJsonApiErrorMessage(error, t('customer.billing.paymentMethod.feedback.updateFailed')),
                  );
                }
              }}
            />
          </Elements>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
